import React, { useEffect, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useStore, withActiveStore } from "@/lib/storeContext";
import { Pagination } from "@/components/Pagination";
import { getPageSize, setPageSize, pageSlice } from "@/lib/listDensity";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { useFeatures } from "@/lib/features";
import { isLodgingOnly } from "@/lib/activityCatalog";

const COLORS = ["#1E2D5B", "#0A0A0A", "#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#14B8A6"];
const paymentLabel = (k) => ({
  cash: "Contanti", card: "Carta", ticket: "Ticket", mixed: "Multiplo",
  assegno: "Assegno", bonifico: "Bonifico", non_riscosso: "Non riscosso", omaggio: "Omaggio",
  gestionale: "Gestionale (non fisc.)",
}[k] || k || "Altro");

const DOC_FILTERS = [
  { v: "all", label: "Tutto" },
  { v: "fiscal", label: "Solo fiscali" },
  { v: "management", label: "Solo gestionali" },
];

const PAY_OPTIONS = [
  { v: "cash", label: "Contanti" },
  { v: "card", label: "Carta" },
  { v: "assegno", label: "Assegno" },
  { v: "ticket", label: "Ticket / Buoni pasto" },
  { v: "non_riscosso", label: "Non riscosso / Segue fattura" },
  { v: "omaggio", label: "Omaggio" },
  { v: "mixed", label: "Pagamento misto" },
  { v: "bonifico", label: "Bonifico" },
];

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return iso(d);
};
const PERIODS = [
  { id: "today", label: "Oggi" },
  { id: "7d", label: "7 giorni" },
  { id: "month", label: "Questo mese" },
  { id: "year", label: "Quest'anno" },
];
function periodDates(id) {
  const today = iso(new Date());
  if (id === "today") return [today, today];
  if (id === "7d") return [addDays(-6), today];
  if (id === "year") return [`${today.slice(0, 4)}-01-01`, today];
  return [`${today.slice(0, 7)}-01`, today];
}

export default function Stats() {
  const { user } = useAuth();
  const isOwner = user?.role === "client_owner" || user?.role === "client" || user?.is_demo;
  const { storeId } = useStore();
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 7) + "-01";
  const [from, setFrom] = useState(monthStart);
  const [to, setTo] = useState(today);
  const [period, setPeriod] = useState("month");
  const [prodPage, setProdPage] = useState(1);
  const [pageSize, setSize] = useState(getPageSize);
  const [docFilter, setDocFilter] = useState("all");
  const [payMethod, setPayMethod] = useState("");
  const [customMethods, setCustomMethods] = useState([]);

  const [summary, setSummary] = useState(null);
  const [byCat, setByCat] = useState([]);
  const [byProd, setByProd] = useState([]);
  const [byPay, setByPay] = useState([]);
  const [gest, setGest] = useState(null); // {count, total} — solo titolare
  const [overview, setOverview] = useState(null);
  const [todayPay, setTodayPay] = useState([]);
  const { modes } = useFeatures();
  const onlyLodging = isLodgingOnly(modes);
  const dedicatedLodging = onlyLodging && (modes || []).includes("alloggio_dedicato") && !(modes || []).includes("bnb");
  const [lodging, setLodging] = useState(null);

  const load = (filter = docFilter, pm = payMethod, dateFrom = from, dateTo = to) => {
    const params = withActiveStore({ date_from: dateFrom, date_to: dateTo, doc_filter: filter });
    if (pm) params.payment_method = pm;
    api.get("/stats/summary", { params }).then((r) => setSummary(r.data));
    api.get("/stats/by-category", { params }).then((r) => setByCat(Array.isArray(r.data) ? r.data : []));
    api.get("/stats/by-product", { params }).then((r) => setByProd(Array.isArray(r.data) ? r.data : []));
    api.get("/stats/by-payment", { params }).then((r) => setByPay(Array.isArray(r.data) ? r.data : []));
    api
      .get(`/reports/management-movements?from_date=${dateFrom}T00:00:00&to_date=${dateTo}T23:59:59${storeId ? `&store_id=${storeId}` : ""}`)
      .then((r) => setGest({ count: r.data.count || 0, total: r.data.total || 0 }))
      .catch(() => setGest(null));
  };

  useEffect(() => {
    if (onlyLodging) {
      api.get("/hotel/stats").then((r) => setLodging(r.data)).catch(() => setLodging(null));
      return;
    }
    load();
    api.get("/tenant/me").then((r) => setCustomMethods(Array.isArray(r.data?.custom_payment_methods) ? r.data?.custom_payment_methods : [])).catch(() => {});
    api.get("/stats/overview", { params: withActiveStore() }).then((r) => setOverview(r.data)).catch(() => {});
    api.get("/stats/by-payment", { params: withActiveStore({ date_from: today, date_to: today }) }).then((r) => setTodayPay(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [storeId, onlyLodging]); // eslint-disable-line

  const applyPeriod = (id) => {
    const [a, b] = periodDates(id);
    setPeriod(id);
    setFrom(a);
    setTo(b);
    setProdPage(1);
    load(docFilter, payMethod, a, b);
  };

  const setFilter = (v) => {
    setDocFilter(v);
    load(v);
  };

  const setPayment = (v) => {
    setPayMethod(v);
    load(docFilter, v);
  };

  const payLabelOf = (v) =>
    PAY_OPTIONS.find((o) => o.v === v)?.label || customMethods.find((m) => m.id === v)?.label || v;

  if (onlyLodging) {
    const s = lodging || {};
    const cards = dedicatedLodging ? [
      ["Clienti", s.guests ?? "—", "anagrafica salvata"],
      ["Arrivi oggi", s.direct_arrivals_today ?? "—", "data di arrivo"],
      ["Giorni del mese", s.direct_nights_month ?? "—", "permanenze indicate"],
      ["Documenti letti", s.documents ?? "—", "con numero documento"],
      ["Da inviare", s.direct_pending ?? "—", "non ancora in Alloggiati"],
    ] : [
      ["Camere", s.rooms ?? "—", "della struttura"],
      ["Occupate oggi", s.occupied ?? "—", "soggiorni in corso"],
      ["Arrivi oggi", s.arrivals_today ?? "—", "check-in"],
      ["Partenze oggi", s.departures_today ?? "—", "check-out"],
      ["Notti del mese", s.nights_month ?? "—", "dai soggiorni arrivati"],
      ["Clienti", s.guests ?? "—", "anagrafica alloggio"],
      ["Documenti letti", s.documents ?? "—", "con numero documento"],
      ["Da inviare", s.pending_alloggiati ?? "—", "soggiorni non ancora in Alloggiati"],
    ];
    return (
      <div className="p-8 lg:p-12">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Statistiche</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">{dedicatedLodging ? "Clienti" : "Struttura"}</h1>
        <p className="text-velta-mute mt-2 mb-8">{dedicatedLodging
          ? "Schede lette dal documento e inviate ad Alloggiati Web e ROSS 1000. I giorni di permanenza li indica l'operatore."
          : "Arrivi, camere e notti di questo alloggio. I documenti letti con la scansione si inviano da Alberghiero ad Alloggiati Web e ROSS 1000."}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map(([label, value, sub]) => (
            <div key={label} className="bg-white border border-velta-border rounded-md p-5">
              <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">{label}</div>
              <div className="font-mono font-bold text-3xl tracking-tighter">{value}</div>
              <div className="text-xs text-velta-mute mt-1">{sub}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Statistiche</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-8">Analisi vendite</h1>

      {/* Riepilogo di OGGI: incasso, scontrini e ripartizione per metodo di pagamento */}
      <div data-testid="stats-today-strip" className="bg-[#1E2D5B] text-white rounded-md p-5 mb-6 flex flex-wrap items-center gap-x-10 gap-y-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-70">Oggi — Incasso</div>
          <div data-testid="stats-today-total" className="font-mono font-bold text-3xl tabular-nums">{formatEUR(overview?.today_total || 0)}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-70">Scontrini</div>
          <div data-testid="stats-today-count" className="font-mono font-bold text-3xl tabular-nums">{overview?.today_count || 0}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-70">Storni</div>
          <div data-testid="stats-today-refunds" className="font-mono font-bold text-3xl tabular-nums">{formatEUR(overview?.today_refund_total || 0)}</div>
          <div className="text-[10px] font-mono opacity-70">{overview?.today_refund_count || 0} documenti</div>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] opacity-70">Per metodo di pagamento</div>
          <div className="flex flex-wrap gap-2 mt-1.5">
            {todayPay.length === 0 ? (
              <span className="text-sm opacity-60">Nessun incasso oggi</span>
            ) : (Array.isArray(todayPay) ? todayPay : []).map((p) => (
              <span key={p.method} data-testid={`stats-today-pay-${p.method}`} className="bg-white/10 border border-white/20 rounded px-2.5 py-1 text-xs font-mono">
                {payLabelOf(p.method)} · {formatEUR(p.total)}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-velta-border rounded-md p-5 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Periodo</label>
          <div className="flex rounded-md border border-velta-border overflow-hidden h-11">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`stats-period-${p.id}`}
                onClick={() => applyPeriod(p.id)}
                className={`px-3 text-xs font-medium whitespace-nowrap ${period === p.id ? "bg-velta-ink text-white" : "bg-white text-velta-mute hover:text-velta-ink"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {isOwner && (
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Documenti</label>
            <div className="flex rounded-md border border-velta-border overflow-hidden h-11">
              {(Array.isArray(DOC_FILTERS) ? DOC_FILTERS : []).map(({ v, label }) => (
                <button
                  key={v}
                  data-testid={`stats-filter-${v}`}
                  onClick={() => setFilter(v)}
                  className={`px-4 text-sm font-medium ${docFilter === v ? "bg-velta-ink text-white" : "bg-white text-velta-mute hover:text-velta-ink"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Pagamento</label>
          <select
            data-testid="stats-filter-payment"
            value={payMethod}
            onChange={(e) => setPayment(e.target.value)}
            className="h-11 bg-white border border-velta-border rounded-md px-3 text-sm outline-none focus:border-[#1E2D5B] focus:ring-1 focus:ring-[#1E2D5B]"
          >
            <option value="">Tutti i pagamenti</option>
            {(Array.isArray(PAY_OPTIONS) ? PAY_OPTIONS : []).map(({ v, label }) => (
              <option key={v} value={v}>{label}</option>
            ))}
            {(Array.isArray(customMethods) ? customMethods : []).map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Dal</label>
          <input data-testid="stats-from-input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 bg-white border border-velta-border rounded-md px-3 font-mono focus:border-[#1E2D5B] focus:ring-1 focus:ring-[#1E2D5B] outline-none" />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Al</label>
          <input data-testid="stats-to-input" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 bg-white border border-velta-border rounded-md px-3 font-mono focus:border-[#1E2D5B] focus:ring-1 focus:ring-[#1E2D5B] outline-none" />
        </div>
        <button data-testid="stats-apply-btn" onClick={() => load()} className="h-11 px-5 bg-velta-ink text-white rounded-md font-medium btn-tactile">Applica filtri</button>
        {summary && (
          <div className="ml-auto text-right">
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">
              Incasso periodo — {DOC_FILTERS.find((f) => f.v === docFilter)?.label}{payMethod ? ` · ${payLabelOf(payMethod)}` : ""}
            </div>
            <div data-testid="stats-summary-total" className="font-heading text-3xl font-bold tabular-nums">{formatEUR(summary.total)}</div>
            <div data-testid="stats-summary-count" className="text-xs font-mono text-velta-mute">{summary.count} documenti</div>
            {(summary.refund_count || 0) > 0 && (
              <div data-testid="stats-summary-refunds" className="text-xs font-mono text-red-600">Storni {summary.refund_count} · {formatEUR(summary.refund_total || 0)}</div>
            )}
          </div>
        )}
      </div>

      {gest && gest.count > 0 && docFilter !== "management" && (
        <div data-testid="stats-gestionale-card" className="bg-white border-2 border-velta-warning/40 rounded-md p-5 mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Movimenti Gestionali (non fiscali) nel periodo</div>
            <div className="text-xs text-velta-mute mt-1">Esclusi dai ricavi fiscali. Dettaglio completo in Gestionale → Movimenti Gestionali.</div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="font-heading text-2xl font-bold">{gest.count}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Movimenti</div>
            </div>
            <div className="text-center">
              <div className="font-heading text-2xl font-bold tabular-nums">{formatEUR(gest.total)}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-velta-mute">Valore</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-velta-border rounded-md p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Vendite per categoria</div>
          <h3 className="font-heading text-xl font-semibold mb-5">Categorie più vendute</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(Array.isArray(byCat) ? byCat : []).slice(0, 8)} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                <XAxis type="number" stroke="#A1A1AA" fontSize={11} fontFamily="IBM Plex Mono" tickFormatter={(v) => `€${v}`} />
                <YAxis type="category" dataKey="category" stroke="#A1A1AA" fontSize={12} width={100} />
                <Tooltip formatter={(v) => formatEUR(v)} contentStyle={{ borderRadius: 6, fontSize: 13 }} />
                <Bar dataKey="total" fill="#1E2D5B" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-velta-border rounded-md p-6">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Metodi di pagamento</div>
          <h3 className="font-heading text-xl font-semibold mb-5">Ripartizione incassi</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={(Array.isArray(byPay) ? byPay : []).map(p => ({ ...p, name: paymentLabel(p.method) }))} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                  {(Array.isArray(byPay) ? byPay : []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => formatEUR(v)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
        <div className="px-6 py-5 border-b border-velta-border">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Articoli</div>
          <h3 className="font-heading text-xl font-semibold">Top articoli venduti</h3>
        </div>
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-velta-bg">
            <tr className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute">
              <th className="px-5 py-3">#</th>
              <th className="px-5 py-3">Articolo</th>
              <th className="px-5 py-3 text-right">Quantità</th>
              <th className="px-5 py-3 text-right">Incasso</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const sliced = pageSlice(byProd, prodPage, pageSize);
              return (Array.isArray(sliced.items) ? sliced.items : []).map((p, i) => (
              <tr key={p.product} className="border-b border-velta-border last:border-0">
                <td className="px-5 py-3 font-mono text-velta-mute">{(sliced.page - 1) * pageSize + i + 1}</td>
                <td className="px-5 py-3 font-medium">{p.product}</td>
                <td className="px-5 py-3 text-right font-mono">{p.qty}</td>
                <td className="px-5 py-3 text-right font-mono font-medium">{formatEUR(p.total)}</td>
              </tr>
              ));
            })()}
            {byProd.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-12 text-center text-velta-mute">Nessuna vendita nel periodo selezionato.</td></tr>
            )}
          </tbody>
        </table>
        {byProd.length > 0 && (
          <div className="px-6 pb-4">
            <Pagination
              page={pageSlice(byProd, prodPage, pageSize).page}
              pages={pageSlice(byProd, prodPage, pageSize).pages}
              total={byProd.length}
              onPage={setProdPage}
              pageSize={pageSize}
              onPageSize={(n) => { setSize(setPageSize(n)); setProdPage(1); }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
